import * as React from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

/** Where this alert is rendered (included in on-screen diagnostics for logs / replays). */
export type SignInBlockedAlertContext = 'RedirectToKeycloak' | 'AuthProvider';

/** Padding and placement: sign-in page vs full-app shell while unauthenticated. */
export type SignInBlockedAlertLayout = 'signIn' | 'appShell';

export type SignInBlockedAlertProps =
  | {
      variant: 'tenant_hostname_lookup_failed';
      hostname: string | null;
      context: SignInBlockedAlertContext;
      layout: SignInBlockedAlertLayout;
    }
  | {
      variant: 'tenant_not_in_keycloak_allowlist';
      hostname: string | null;
      resolvedTenantId: string;
      context: SignInBlockedAlertContext;
      layout: SignInBlockedAlertLayout;
    };

const diagnosticPreSx = {
  m: 0,
  p: 1,
  bgcolor: 'action.hover',
  borderRadius: 1,
  typography: 'caption',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  color: 'text.secondary',
} as const;

/**
 * Stable `signInBlockReason` values shown on auth error screens so support and
 * engineers can correlate session replays, screenshots, and application logs.
 */
export const SIGN_IN_BLOCK_REASON = {
  tenantHostnameLookupFailed: 'tenant_hostname_lookup_failed',
  tenantNotInKeycloakAllowlist: 'tenant_not_in_keycloak_allowlist',
} as const;

/** Env var that must list `resolvedTenantId` when sign-in is blocked for that reason. */
export const WORK_PERMIT_KEYCLOAK_TENANTS_ENV_VAR =
  'NEXT_PUBLIC_WORK_PERMIT_AUTH_KEYCLOAK_TENANTS' as const;

function buildDiagnosticDetails(props: SignInBlockedAlertProps): string {
  const host = props.hostname ?? '';
  if (props.variant === 'tenant_hostname_lookup_failed') {
    return `signInBlockReason=${SIGN_IN_BLOCK_REASON.tenantHostnameLookupFailed}
hostname=${host}
context=${props.context}`;
  }
  return `signInBlockReason=${SIGN_IN_BLOCK_REASON.tenantNotInKeycloakAllowlist}
hostname=${host}
resolvedTenantId=${props.resolvedTenantId}
checkEnvVar=${WORK_PERMIT_KEYCLOAK_TENANTS_ENV_VAR}
context=${props.context}`;
}

function SignInDiagnosticPre(props: { details: string }): React.ReactElement {
  return (
    <Box
      aria-label="Sign-in diagnostic details for support and engineering"
      component="pre"
      sx={diagnosticPreSx}
    >
      {props.details}
    </Box>
  );
}

/**
 * User-facing sign-in blockage message with a copy-pastable diagnostic block
 * for support, logs, and session replays.
 */
export function SignInBlockedAlert(
  props: SignInBlockedAlertProps,
): React.ReactElement {
  const outerSx =
    props.layout === 'signIn'
      ? { p: 2, maxWidth: 560, mx: 'auto' }
      : { p: 3, maxWidth: 560, mx: 'auto', mt: 4 };

  const diagnosticDetails = buildDiagnosticDetails(props);

  if (props.variant === 'tenant_hostname_lookup_failed') {
    return (
      <Box sx={outerSx}>
        <Alert severity="error">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="body2">
              Could not look up the tenant for this host (
              {props.hostname || 'unknown'}
              ). Check your network connection and that the API is reachable,
              then try again.
            </Typography>
            <SignInDiagnosticPre details={diagnosticDetails} />
          </Box>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={outerSx}>
      <Alert severity="error">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2">
            We can&apos;t sign you in from this site yet. This usually means
            your organization isn&apos;t connected to this app for this web
            address, or sign-in still needs to be turned on for your
            environment.
          </Typography>
          <Typography variant="body2">
            Please contact your administrator or IT help desk. They can enable
            access or update how your site is linked to sign-in.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            If support asks what you were using: this page is on{' '}
            {props.hostname ? (
              <strong>{props.hostname}</strong>
            ) : (
              'this address'
            )}{' '}
            · internal reference: <code>{props.resolvedTenantId}</code>.
          </Typography>
          <SignInDiagnosticPre details={diagnosticDetails} />
        </Box>
      </Alert>
    </Box>
  );
}
